import React, { useEffect } from 'react';

/**
 * ConfirmModal - Reusable premium confirmation dialog
 * Usage: <ConfirmModal open={bool} title="..." message="..." onConfirm={fn} onCancel={fn} variant="danger|warning|info" />
 */
const ConfirmModal = ({
    open,
    title = 'Confirmation',
    message = 'Êtes-vous sûr ?',
    confirmLabel = 'Confirmer',
    cancelLabel = 'Annuler',
    onConfirm,
    onCancel,
    variant = 'danger', // 'danger' | 'warning' | 'info'
    loading = false,
    children,
}) => {
    // Close on Escape key
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onCancel?.(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onCancel]);

    if (!open) return null;

    const colors = {
        danger: { icon: '🗑️', btnClass: 'btn-danger', accent: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
        warning: { icon: '⚠️', btnClass: 'btn-warning', accent: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
        info: { icon: 'ℹ️', btnClass: 'btn-primary', accent: 'var(--primary-600)', bg: 'var(--primary-50)', border: 'var(--primary-100)' },
    };
    const c = colors[variant] || colors.danger;

    return (
        <div
            className="modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
            style={{ zIndex: 10000, animation: 'fadeIn 0.15s ease' }}
        >
            <div
                className="modal"
                style={{
                    maxWidth: '440px',
                    width: '90%',
                    borderRadius: 'var(--radius-lg)',
                    overflow: 'hidden',
                    animation: 'slideUp 0.2s ease',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                }}
            >
                {/* Accent top border */}
                <div style={{ height: '4px', background: c.accent }} />

                <div style={{ padding: '28px 28px 0' }}>
                    {/* Icon + Title */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                        <div style={{
                            width: '48px', height: '48px', borderRadius: '12px',
                            background: c.bg, border: `1px solid ${c.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '22px', flexShrink: 0
                        }}>
                            {c.icon}
                        </div>
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)', margin: 0 }}>
                                {title}
                            </h3>
                        </div>
                    </div>

                    {/* Message or Children content */}
                    {children ? children : (
                        <p style={{
                            fontSize: '14px',
                            color: 'var(--text-secondary)',
                            lineHeight: '1.6',
                            marginBottom: '24px',
                            paddingLeft: '62px',
                        }}>
                            {message}
                        </p>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    padding: '16px 28px 24px',
                    background: 'var(--surface-2)',
                    borderTop: '1px solid var(--border)',
                }}>
                    <button
                        className="btn btn-ghost"
                        onClick={onCancel}
                        disabled={loading}
                        style={{ minWidth: '100px' }}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        className={`btn ${c.btnClass}`}
                        onClick={onConfirm}
                        disabled={loading}
                        style={{ minWidth: '120px' }}
                    >
                        {loading ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                                En cours...
                            </span>
                        ) : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmModal;
